// filepath: apps/web/src/features/proc/ExecutionTracePage.tsx
//
// FR-UI-PROC-1 AC-1 — page chrome for the execution-trace viewer.
//
// Thin wrapper around <ExecutionTraceViewer />. Adds page chrome
// (title + subtitle + back-to-dashboard link). The page reads
// `:instanceId` from `window.location.pathname` via the route
// shell (ProcRoute.tsx); this component just consumes it as a prop.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ExecutionTraceViewer } from "./ExecutionTraceViewer";

export interface ExecutionTracePageProps {
  /** Process instance UUID. */
  instanceId: string;
  /**
   * Optional click-through to the dashboard. When supplied, the
   * page renders a back-link. Typical implementation navigates
   * back to `/processes/instances/failed`.
   */
  onBackToDashboard?: () => void;
}

export function ExecutionTracePage({
  instanceId,
  onBackToDashboard,
}: ExecutionTracePageProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <main
      data-testid="execution-trace-page"
      aria-labelledby="execution-trace-page-title"
      className="execution-trace-page"
    >
      <header className="execution-trace-page-header">
        {onBackToDashboard !== undefined && (
          <button
            type="button"
            data-testid="execution-trace-page-back"
            className="execution-trace-page-back"
            onClick={onBackToDashboard}
          >
            {t("common.back", "Back")}
          </button>
        )}
        <h1
          id="execution-trace-page-title"
          data-testid="execution-trace-page-title"
          className="execution-trace-page-title"
        >
          {t("trace.title", "Execution trace")}
        </h1>
        <p className="execution-trace-page-subtitle">
          {t(
            "trace.subtitle",
            "Step log for the selected process instance, ordered chronologically.",
          )}
        </p>
      </header>
      <ExecutionTraceViewer instanceId={instanceId} />
    </main>
  );
}
